import { useEffect, useState } from 'react';
import { ConfigProvider, Input, InputNumber, Modal } from 'antd';
import heIL from 'antd/locale/he_IL';
import toast from 'react-hot-toast';
import { useSavePettyCash } from '../../hooks/usePettyCash';
import type { PettyCash } from '@deposits/shared';

interface Props {
  open: boolean;
  /** null = open a new petty cash for the selected project. */
  editing: PettyCash | null;
  /** Edit only: whether the opening balance can still change. */
  openingBalanceEditable: boolean;
  projectId: number | null;
  projectName: string | null;
  onClose: () => void;
}

export function PettyCashFormModal({
  open,
  editing,
  openingBalanceEditable,
  projectId,
  projectName,
  onClose,
}: Props) {
  const save = useSavePettyCash();
  const [name, setName] = useState('');
  const [opening, setOpening] = useState<number>(0);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? (projectName ? `קופה קטנה · ${projectName}` : 'קופה קטנה'));
    setOpening(editing ? Number(editing.opening_balance) : 0);
  }, [open, editing, projectName]);

  const submit = () => {
    if (!name.trim()) {
      toast.error('יש להזין שם לקופה');
      return;
    }
    const body = { name: name.trim(), opening_balance: opening };
    save.mutate(
      editing
        ? { id: editing.id, update: body }
        : { create: { ...body, project_id: projectId ?? undefined } },
      {
        onSuccess: () => {
          toast.success(editing ? 'הקופה הקטנה עודכנה' : 'הקופה הקטנה נפתחה');
          onClose();
        },
        onError: (err) => toast.error(err.message || 'שגיאה בשמירה'),
      },
    );
  };

  return (
    <Modal
      open={open}
      title={editing ? 'עריכת קופה קטנה' : 'פתיחת קופה קטנה'}
      okText={editing ? 'שמור' : 'פתח קופה'}
      cancelText="ביטול"
      onOk={submit}
      onCancel={onClose}
      confirmLoading={save.isPending}
      destroyOnClose
    >
      <ConfigProvider locale={heIL}>
        <div className="flex flex-col gap-4 pt-2">
          <div>
            <div className="text-small text-main-text mb-1">שם הקופה</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </div>
          <div>
            <div className="text-small text-main-text mb-1">יתרת פתיחה</div>
            <InputNumber
              value={opening}
              onChange={(v) => setOpening(Number(v ?? 0))}
              min={0}
              addonAfter="₪"
              style={{ width: '100%' }}
              disabled={!!editing && !openingBalanceEditable}
            />
            <div className="text-small text-secondary-text mt-1">
              {editing && !openingBalanceEditable
                ? 'אי אפשר לשנות את יתרת הפתיחה אחרי שנרשמו תנועות.'
                : 'כמה מזומן יש בקופה היום, לפני הטעינה הראשונה.'}
            </div>
          </div>
        </div>
      </ConfigProvider>
    </Modal>
  );
}
